import React, { useEffect, useState } from "react";
import { Container, Form, Row, Col, Button, Card } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import reportApi from "../../services/reportapi";
import branchApi from "../../services/branchapi";
import userApi from "../../services/userapi";
import Loader from "../loader/loader";
import DynamicBreadcrumb from "../header/breadcrumb";

const PickerPackerItemChangesReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    const [filters, setFilters] = useState({
        from_date: moment().format("YYYY-MM-DD"),
        to_date: moment().format("YYYY-MM-DD"),
        branch_id: "",
        order_id: "",
        action_type: "",
        user_id: "",
        search: "",
    });

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchBranches = async () => {
        const res = await branchApi.getAllBranches({
            limit: 200,
            page: 0,
            status: 1,
            sortModel: [{ field: "branchName", sort: "asc" }],
        });
        setBranches(res?.data?.result || []);
    };

    const fetchUsers = async () => {
        const res = await userApi.getUsers({
            limit: 200,
            page: 0,
            status: 1,
            userType: "",
            search: "",
            sortModel: [{ field: "name", sort: "asc" }],
        });

        const list = res?.data?.result || res?.data?.data?.result || [];
        setUsers(list);
    };

    const fetchReport = async (page = paginationModel.page, pageSize = paginationModel.pageSize) => {
        setLoading(true);
        try {
            const res = await reportApi.getPickerPackerItemChanges({
                ...filters,
                page,
                limit: pageSize,
            });

            const data = res?.data || {};
            setRows((data.rows || []).map((x, i) => ({ id: x.id || i + 1, ...x })));
            setTotalRecords(data.totalRecords || 0);
        } catch (err) {
            console.error("Picker packer item changes report error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchReport(paginationModel.page, paginationModel.pageSize);
    }, [paginationModel.page, paginationModel.pageSize]);

    const handleFilter = () => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchReport(0, paginationModel.pageSize);
    };

    const columns = [
        { field: "created_at", headerName: "Date", flex: 1.2 },
        { field: "branch_name", headerName: "Branch", flex: 1.2 },
        { field: "order_showid", headerName: "Order ID", flex: 1 },
        { field: "action_type", headerName: "Action", flex: 1 },
        { field: "item_ref_code", headerName: "Ref Code", flex: 1 },
        { field: "product_name", headerName: "Product", flex: 2 },
        { field: "variant", headerName: "Variant", flex: 1 },
        { field: "old_qty", headerName: "Old Qty", flex: 0.7 },
        { field: "new_qty", headerName: "New Qty", flex: 0.7 },
        { field: "reason", headerName: "Reason", flex: 1.5 },
        { field: "changed_by_name", headerName: "Changed By", flex: 1.2 },
    ];

    return (
        <Container fluid className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={[{ name: "New / Updated / Removed Items Report", url: null }]} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>New / Updated / Removed Items Report</h5>

                <Button
                    variant="outline-secondary"
                    onClick={() => navigate("/admin/reports/picker-packer-activity")}
                >
                    Back to Summary
                </Button>
            </div>

            <Form className="mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branch_id}
                            onChange={(e) => updateFilters("branch_id", e.target.value)}
                        >
                            <option value="">All Branches</option>
                            {branches.map((branch) => (
                                <option key={branch.branchId} value={branch.branchId}>
                                    {branch.branchName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => updateFilters("from_date", e.target.value)}
                        />
                    </Col>

                    <Col md={3}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => updateFilters("to_date", e.target.value)}
                        />
                    </Col>

                    <Col md={3}>
                        <Form.Label>Order ID</Form.Label>
                        <Form.Control
                            value={filters.order_id}
                            placeholder="Order ID"
                            onChange={(e) => updateFilters("order_id", e.target.value)}
                        />
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md={3}>
                        <Form.Label>Action</Form.Label>
                        <Form.Select
                            value={filters.action_type}
                            onChange={(e) => updateFilters("action_type", e.target.value)}
                        >
                            <option value="">All Actions</option>
                            <option value="ADD_ITEM">New Item</option>
                            <option value="ADD">New Item Old Key</option>
                            <option value="QTY_UPDATE">Qty Updated</option>
                            <option value="UPDATE">Updated Old Key</option>
                            <option value="REMOVE_ITEM">Removed Item</option>
                            <option value="REMOVE">Removed Old Key</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>User</Form.Label>
                        <Form.Select
                            value={filters.user_id}
                            onChange={(e) => updateFilters("user_id", e.target.value)}
                        >
                            <option value="">All Users</option>
                            {users.map((user) => (
                                <option key={user.userId} value={user.userId}>
                                    {user.name || user.userName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>Search</Form.Label>
                        <Form.Control
                            value={filters.search}
                            placeholder="Product / Ref code / User"
                            onChange={(e) => updateFilters("search", e.target.value)}
                        />
                    </Col>

                    <Col md={3} className="d-flex align-items-end">
                        <Button onClick={handleFilter}>Filter</Button>
                    </Col>
                </Row>
            </Form>

            {loading && <Loader />}

            <Card>
                <Card.Header>New / Updated / Removed Items</Card.Header>
                <Card.Body>
                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            autoHeight
                            paginationMode="server"
                            rowCount={totalRecords}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[25, 50, 100]}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                        />
                    </Box>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PickerPackerItemChangesReport;